import { useCallback, useEffect, useRef, useState } from 'react';

export type ResizeAxis = 'x' | 'y';
export type ResizeEdge = 'left' | 'right' | 'top' | 'bottom';

type UseResizeOptions = {
  initial: number;
  min: number;
  max?: number;
  axis?: ResizeAxis;
  edge?: ResizeEdge;
};

type UseResizeReturn = {
  size: number;
  isResizing: boolean;
  enableResize: (e: MouseEvent | React.MouseEvent) => void;
  resizeBy: (delta: number) => void;
  resizeTo: (size: number) => void;
  // Imperatively start a resize interaction using a specific starting size.
  // Useful when the visual size has not yet caught up to a just-updated "initial".
  beginResizeFrom: (e: MouseEvent | React.MouseEvent, startingSize: number) => void;
};

const clamp = (value: number, min: number, max?: number): number => {
  if (max != null) {
    return Math.min(Math.max(value, min), max);
  }
  return Math.max(value, min);
};

const useResize = (options: UseResizeOptions): UseResizeReturn => {
  const { initial, min, max, axis = 'x', edge = 'right' } = options;
  const [size, setSize] = useState<number>(initial);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const initialPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialSizeRef = useRef<number>(initial);
  const rafIdRef = useRef<number | null>(null);
  const pendingSizeRef = useRef<number | null>(null);

  const enableResize = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      const evt = e as MouseEvent;
      initialPosRef.current = { x: evt.clientX, y: evt.clientY };
      initialSizeRef.current = size;
      setIsResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
    },
    [axis, size]
  );

  const beginResizeFrom = useCallback(
    (e: MouseEvent | React.MouseEvent, startingSize: number) => {
      const evt = e as MouseEvent;
      // Seed both the ref and state with the caller-provided starting size so
      // the first mousemove uses the correct baseline without visual jump.
      initialPosRef.current = { x: evt.clientX, y: evt.clientY };
      initialSizeRef.current = startingSize;
      setSize(startingSize);
      setIsResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
    },
    [axis]
  );

  const stopResize = useCallback(() => {
    setIsResizing(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  const resizeTo = useCallback(
    (nextSize: number) => {
      const clamped = clamp(nextSize, min, max);
      initialSizeRef.current = clamped;
      setSize(clamped);
    },
    [max, min]
  );

  const resizeBy = useCallback(
    (delta: number) => {
      setSize((current) => {
        const clamped = clamp(current + delta, min, max);
        initialSizeRef.current = clamped;
        return clamped;
      });
    },
    [max, min]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const dx = e.clientX - initialPosRef.current.x;
      const dy = e.clientY - initialPosRef.current.y;

      let newSize = initialSizeRef.current;
      if (axis === 'x') {
        if (edge === 'right') {
          newSize = initialSizeRef.current + dx;
        } else if (edge === 'left') {
          newSize = initialSizeRef.current + (initialPosRef.current.x - e.clientX);
        }
      } else {
        if (edge === 'top') {
          newSize = initialSizeRef.current + (initialPosRef.current.y - e.clientY);
        } else {
          newSize = initialSizeRef.current + dy;
        }
      }
      const clamped = clamp(newSize, min, max);
      // Throttle state updates to animation frames for smoother dragging
      pendingSizeRef.current = clamped;
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(() => {
          if (pendingSizeRef.current != null) {
            setSize(pendingSizeRef.current);
          }
          pendingSizeRef.current = null;
          rafIdRef.current = null;
        });
      }
    },
    [axis, edge, isResizing, min, max]
  );

  useEffect(() => {
    if (!isResizing) return;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopResize);
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing, onMouseMove, stopResize]);

  useEffect(() => {
    setSize(initial);
    initialSizeRef.current = initial;
  }, [initial]);

  useEffect(() => {
    setSize((current) => clamp(current, min, max));
  }, [min, max]);

  return { size, isResizing, enableResize, beginResizeFrom, resizeBy, resizeTo };
};

export default useResize;
